import { get, set } from 'idb-keyval';
import type { Book, LibrarySource, ProgressFile, Track } from '../types';

/**
 * Track lengths, remembered across sessions.
 *
 * Drive does not report how long an audio file is, so a freshly scanned library
 * knows no lengths at all — and without them the library cannot show how far
 * through a book you are. Lengths learned while playing, or measured in the
 * background, are kept here and applied every time the library loads.
 *
 * Keyed by file id and size, so a file replaced in Drive is measured again.
 */

const STORE_KEY = 'shelf:durations';
const MEASURE_TIMEOUT_MS = 20_000;

let known: Promise<Record<string, number>> | null = null;

const keyOf = (track: Track) => `${track.id}:${track.sizeBytes}`;

function loadKnown(): Promise<Record<string, number>> {
  known ??= (async () => {
    try {
      return (await get<Record<string, number>>(STORE_KEY)) ?? {};
    } catch {
      return {};
    }
  })();
  return known;
}

/** The same books, with every remembered track length filled in. */
export async function withKnownDurations(books: Book[]): Promise<Book[]> {
  const lengths = await loadKnown();
  return books.map((book) => {
    if (book.tracks.every((t) => t.durationSec || !lengths[keyOf(t)])) return book;
    return {
      ...book,
      tracks: book.tracks.map((t) => (t.durationSec ? t : { ...t, durationSec: lengths[keyOf(t)] })),
    };
  });
}

export async function rememberDuration(track: Track, seconds: number): Promise<void> {
  if (!Number.isFinite(seconds) || seconds <= 0) return;
  const lengths = await loadKnown();
  if (lengths[keyOf(track)] === seconds) return;
  lengths[keyOf(track)] = seconds;
  try {
    await set(STORE_KEY, lengths);
  } catch {
    /* still known for this session */
  }
}

/** The same books, with one track's length set wherever that track appears. */
export function applyDuration(books: Book[], trackId: string, seconds: number): Book[] {
  return books.map((book) =>
    book.tracks.some((t) => t.id === trackId && t.durationSec !== seconds) ?
      {
        ...book,
        tracks: book.tracks.map((t) => (t.id === trackId ? { ...t, durationSec: seconds } : t)),
      }
    : book,
  );
}

/**
 * Measures, in the background, one track of every book you have started whose
 * length cannot yet be estimated — the track you are on. One measured track is
 * enough for the rest of the book to be estimated from file sizes, so this is
 * a single small read per book, and only ever once, since results are kept.
 */
export async function measureStartedBooks(
  books: Book[],
  progress: ProgressFile,
  source: LibrarySource,
  onMeasured: (trackId: string, seconds: number) => void,
): Promise<void> {
  if (!source.metadataUrl) return;

  for (const book of books) {
    const entry = progress.books[book.id];
    if (!entry || entry.finished) continue;
    if (book.tracks.some((t) => t.durationSec && t.durationSec > 0)) continue;

    const track = book.tracks[entry.trackIndex] ?? book.tracks[0];
    if (!track) continue;

    const url = source.metadataUrl(track);
    // No cheap way to read headers right now (e.g. the streaming worker is not
    // running); the next library load will try again.
    if (!url) return;

    const seconds = await measure(url);
    if (seconds === null) continue;
    await rememberDuration(track, seconds);
    onMeasured(track.id, seconds);
  }
}

/** Loads only a file's header into a throwaway audio element to read its
 *  length, then cancels the rest of the download. */
function measure(url: string): Promise<number | null> {
  return new Promise((resolve) => {
    const el = new Audio();
    el.preload = 'metadata';
    el.muted = true;

    let settled = false;
    const finish = (seconds: number | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      el.removeAttribute('src');
      el.load();
      resolve(seconds);
    };

    const timer = setTimeout(() => finish(null), MEASURE_TIMEOUT_MS);
    el.addEventListener('loadedmetadata', () =>
      finish(Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null),
    );
    el.addEventListener('error', () => finish(null));
    el.src = url;
  });
}
